import { Routes, Route, Navigate } from 'react-router';

import Splash from "@pages/Splash/Splash";
import OnBoarding from "@pages/OnBoarding/OnBoarding";
import LoginWallet from "@pages/LoginWallet/LoginWallet";
import CreateWallet from "@pages/CreateWallet/CreateWallet";
import Home from "@pages/Home/Home";
import TransactionDetail from "@pages/TransactionDetail/TransactionDetail";
import Settings from "@pages/Settings/Settings";
import TopUpMethod from "@pages/TopUpMethod/TopUpMethod";
import TopUpCryptoSelectAsset from "@pages/TopUpCryptoSelectAsset/TopUpCryptoSelectAsset";
import TopUpCryptoDepositAddress from "@pages/TopUpCryptoDepositAddress/TopUpCryptoDepositAddress";
import TopUpCryptoPreview from '@pages/TopUpCryptoPreview/TopUpCryptoPreview';
import TopUpBankSelect from "@pages/TopUpBankSelect/TopUpBankSelect";
import TopUpBankInputAmount from "@pages/TopUpBankInputAmount/TopUpBankInputAmount";
import TopUpBankVirtualAccount from "@pages/TopUpBankVirtualAccount/TopUpBankVirtualAccount";
import Transfer from "@pages/Transfer/Transfer";
import Language from "@pages/Language/Language";
import Redeem from "@pages/Redeem/Redeem";
import RedeemBank from "@pages/RedeemBank/RedeemBank";
import RedeemEWallet from "@pages/RedeemEWallet/RedeemEWallet";
import Security from "@pages/Security/Security";
import BankAndAddress from "@pages/BankAndAddress/BankAndAddress";
import ChangePIN from "@pages/ChangePIN/ChangePIN";
import AuthenticationWrapper from "@pages/Authentication/AuthenticationWrapper";
import ManageBank from "@pages/ManageBank/ManageBank";
import ManageAddress from "@pages/ManageAddress/ManageAddress";
import ManageEWallet from "@pages/ManageEWallet/ManageEWallet";
import P2PTransfer from "@pages/P2PTransfer/P2PTransfer";
import P2POneTimeTransfer from "@pages/P2POneTimeTransfer/P2POneTimeTransfer";
import RedeemConfirmationWrapper from "@pages/RedeemConfirmation/RedeemConfirmationWrapper";
import CompletedProcessWrapper from "@pages/CompletedProcess/CompletedProcessWrapper";
import QRScanner from "@pages/QRScanner/QRScanner";
import P2PTransferConfirmationWrapper from "@pages/P2PTransferConfirmation/P2PTransferConfirmationWrapper";
import ShowRecoveryPhrase from '@pages/ShowRecoveryPhrase/ShowRecoveryPhrase';
import ReferralCode from '@pages/ReferralCode/ReferralCode';
import Account from '@pages/Account/Account';
import AccountUpgrade from '@pages/AccountUpgrade/AccountUpgrade';
import BankTransferPayment from '@pages/BankTransferPayment/BankTransferPayment';
import BankTransferPaymentConfirmation from '@pages/BankTransferPaymentConfirmation/BankTransferPaymentConfirmation';
import BankTransferPaymentReceipt from "@pages/BankTransferPaymentReceipt/BankTransferPaymentReceipt";
import ProtectedRoute from "./ProtectedRoute";
import PaymentLink from "@pages/PaymentLink/PaymentLink";
import PaymentLinkInputAmount from "@pages/PaymentLinkInputAmount/PaymentLinkInputAmount";
import MerchantInformation from "@pages/MerchantInformation/MerchantInformation";
import PaymentLinkSummary from "@pages/PaymentLinkSummary/PaymentLinkSummary";
import PaymentLinkGenerated from "@pages/PaymentLinkGenerated/PaymentLinkGenerated";
import SumsubKYC from "@pages/SumsubKYC/SumsubKYC";
import Biometrics from "@pages/Biometrics/Biometrics";
import ClaimReward from "@pages/ClaimReward/ClaimReward";
import PaymentPoint from "@pages/PaymentPoint/PaymentPoint";
import PhoneCredit from "@pages/PhoneCredit/PhoneCredit";
import PhoneBill from "@pages/PhoneBill/PhoneBill";
import PhoneData from "@pages/PhoneData/PhoneData";
import ESIMCredit from "@pages/ESIMCredit/ESIMCredit";
import ESIMData from "@pages/ESIMData/ESIMData";
import BPJS from "@pages/BPJS/BPJS";
import PLN from "@pages/PLN/PLN";
import InternetCableTV from "@pages/InternetCableTV/InternetCableTV";
import InternetCableTVDetail from "@pages/InternetCableTVDetail/InternetCableTVDetail";
import PDAM from "@pages/PDAM/PDAM";
import BuyESIM from "@pages/BuyESIM/BuyESIM";
import BuyESIMDetails from "@pages/BuyESIMDetails/BuyESIMDetails";
import PaymentConfirm from "@pages/PaymentConfirm/PaymentConfirm";
import PaymentSummary from "@pages/PaymentSummary/PaymentSummary";

import PINLock from "@pages/PINLock/PINLock";
import { useAuth } from '../contexts/AuthContext';
import AdvanceOptions from '@pages/AdvanceOptions/AdvanceOptions';
import LegalCompliance from '@pages/LegalCompliance/LegalCompliance';
import TemporaryTokenMigrationProgress from '@pages/TemporaryTokenMigrationProgress/TemporaryTokenMigrationProgress';
import TemporaryTokenMigrationRouteGuard from '@components/TemporaryTokenMigration/TemporaryTokenMigrationRouteGuard/TemporaryTokenMigrationRouteGuard';

// FallbackRedirect component to handle redirects for non-existent routes
const FallbackRedirect = () => {
  const { isAuthenticated, isAuthReady } = useAuth();

  if (!isAuthReady) {
    return null;
  }

  // Redirect to home if authenticated, otherwise to onboarding
  return isAuthenticated ? <Navigate to="/home" replace /> : <Navigate to="/onboarding" replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* PIN Lock route */}
      <Route path="pin-lock" element={<PINLock />} />

      {/* Root route - redirects to home if authenticated, otherwise to onboarding */}
      <Route index element={<Splash />} />

      <Route path="onboarding" element={<OnBoarding />} />
      <Route path="login" element={<LoginWallet />} />
      <Route path="create" element={<CreateWallet />} />
      <Route path="completed/:type" element={<CompletedProcessWrapper />} />

      {/* Temporary token migration - not protected, accessible during migration */}
      <Route path="token-migration-lock" element={<TemporaryTokenMigrationProgress />} />

      {/* Protected routes - require authentication */}
      <Route element={<ProtectedRoute />}>
        {/* TEMPORARY: Wrap all protected routes with migration guard */}
        <Route element={<TemporaryTokenMigrationRouteGuard />}>
          <Route path="home" element={<Home />} />

          <Route path="transaction-detail/" element={<TransactionDetail />} />
          <Route path="authentication" element={<AuthenticationWrapper />} />

          <Route path="settings" element={<Settings />} />
          <Route path="advance-options" element={<AdvanceOptions />} />
          <Route path="account-upgrade" element={<AccountUpgrade />} />
          <Route path="language" element={<Language />} />
          <Route path="account" element={<Account />} />
          <Route path="referral-code" element={<ReferralCode />} />
          <Route path="show-recovery-phrase" element={<ShowRecoveryPhrase />} />
          <Route path="security" element={<Security />} />
          <Route path="change-pin" element={<ChangePIN />} />
          <Route path="biometrics" element={<Biometrics />} />
          <Route path="legal-compliance" element={<LegalCompliance />} />

          <Route path="bank-and-address" element={<BankAndAddress />} />
          <Route path="manage-bank" element={<ManageBank />} />
          <Route path="manage-address" element={<ManageAddress />} />
          <Route path="manage-e-wallet" element={<ManageEWallet />} />

          <Route path="top-up-method" element={<TopUpMethod />} />
          <Route path="top-up-crypto-select-asset" element={<TopUpCryptoSelectAsset />} />
          <Route path="top-up-crypto-deposit-address" element={<TopUpCryptoDepositAddress />} />
          <Route path="top-up-crypto-preview" element={<TopUpCryptoPreview />} />
          <Route path="top-up-bank-select" element={<TopUpBankSelect />} />
          <Route path="top-up-bank-input-amount" element={<TopUpBankInputAmount />} />
          <Route path="top-up-bank-virtual-account" element={<TopUpBankVirtualAccount />} />

          <Route path="transfer" element={<Transfer />} />

          <Route path="redeem" element={<Redeem />} />
          <Route path="redeem-bank" element={<RedeemBank />} />
          <Route path="redeem-ewallet" element={<RedeemEWallet />} />
          <Route path="redeem-confirmation/:type" element={<RedeemConfirmationWrapper />} />

          <Route path="p2p-transfer" element={<P2PTransfer />} />
          <Route path="p2p-transfer-confirmation/:type" element={<P2PTransferConfirmationWrapper />} />
          <Route path="p2p-one-time-transfer" element={<P2POneTimeTransfer />} />
          <Route path="qr-scanner" element={<QRScanner />} />

          <Route path="bank-transfer-payment" element={<BankTransferPayment />} />
          <Route path="bank-transfer-payment-confirmation" element={<BankTransferPaymentConfirmation />} />
          <Route path="bank-transfer-payment-receipt" element={<BankTransferPaymentReceipt />} />

          <Route path="payment-link" element={<PaymentLink />} />
          <Route path="payment-link-input-amount" element={<PaymentLinkInputAmount />} />
          <Route path="payment-link-summary" element={<PaymentLinkSummary />} />
          <Route path="payment-link-generated" element={<PaymentLinkGenerated />} />
          <Route path="merchant-information" element={<MerchantInformation />} />
          <Route path="sumsub-kyc" element={<SumsubKYC />} />

          <Route path="claim-reward" element={<ClaimReward />} />

          <Route path="payment-point" element={<PaymentPoint />} />
          <Route path="phone-credit" element={<PhoneCredit />} />
          <Route path="phone-billing" element={<PhoneBill />} />
          <Route path="phone-data" element={<PhoneData />} />
          <Route path="esim-credit" element={<ESIMCredit />} />
          <Route path="esim-data" element={<ESIMData />} />
          <Route path="bpjs" element={<BPJS />} />
          <Route path="pln" element={<PLN />} />
          <Route path="internet-cable-tv" element={<InternetCableTV />} />
          <Route path="internet-cable-tv-detail" element={<InternetCableTVDetail />} />
          <Route path="pdam" element={<PDAM />} />
          <Route path="buy-esim" element={<BuyESIM />} />
          <Route path="buy-esim-details" element={<BuyESIMDetails />} />
          <Route path="payment-confirm" element={<PaymentConfirm />} />
          <Route path="payment-summary" element={<PaymentSummary />} />
        </Route>
      </Route>

      {/* Catch-all route - redirects to home if authenticated, otherwise to onboarding */}
      <Route path="*" element={<FallbackRedirect />} />
    </Routes>
  );
};

export default AppRoutes;// In routes/index.tsx
